import { HeadingText } from '../shared/HeadingText';
import { usePages, useSite } from '../../contexts/SiteContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { teamOnSite } from '../../core/rules/salonDays';
import { fill } from '../../utils/copy';
import { pageActions } from '../../data/pageActions';
import { quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';

/**
 * "Ook in de salon": de collega's naast Sarah, op de salonpagina, want daar kies je bij wie je
 * komt. Alleen wie Sarah in Instellingen › Team op "Toon op de salonpagina" zet; zonder zulke
 * collega's staat het blok er niet (de hero noemt Sarah al).
 *
 * Per collega: portret, naam, één zin, de alinea en welke behandelingen zij doet. Eén collega:
 * portret en tekst naast elkaar over de volle breedte; meer: twee kolommen.
 */
/** "Anna de Vries" → "AV": voor een collega die nog geen foto heeft. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase();
}
export function SalonTeam() {
  const pages = usePages();
  const site = useSite();
  const {
    treatments
  } = useCatalog();
  const {
    openBooking
  } = useOverlayActions();
  const copy = pages.treatments.team;
  const colleagues = teamOnSite(site.staff);
  if (colleagues.length === 0) return null;
  const doesFor = (allTreatments: boolean, slugs: string[]) => allTreatments ? ['Alle behandelingen'] : treatments.filter(t => slugs.includes(t.slug)).map(t => t.name);
  const single = colleagues.length === 1;
  return <Section tone="sand" labelledBy="salon-team" contained={false} data-id="element-8516" data-mp="2yhyo2re3g9ld">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8517" data-mp="20zouffu7s2ht">
        <div className="max-w-2xl" data-id="element-8518" data-mp="25zb6vn27xepk">
          <h2 id="salon-team" className={`${sectionHeading} text-ink`} data-id="element-8519" data-mp="25y9vtil7cs7u">
            <HeadingText heading={copy.heading} data-id="element-8520" data-mp="1xyd9tlls1hih" />
          </h2>
          {copy.body && <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-8521" data-mp="1szsb5qm6b5w5">{fill(copy.body)}</p>}
        </div>

        <ul className={`mt-12 grid gap-12 lg:mt-14 ${single ? '' : 'md:grid-cols-2 md:gap-10'}`} data-id="element-8522" data-mp="27oooa233793m">
          {colleagues.map(s => {
          const does = doesFor(s.allTreatments, s.treatments);
          return <li key={s.id} data-id="element-8523" data-mp="1ez7rks8fp61l">
                <article aria-label={s.name} className={`grid gap-6 ${single ? 'sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16' : 'grid-cols-[7rem_minmax(0,1fr)] sm:grid-cols-[9rem_minmax(0,1fr)]'}`} data-id="element-8524" data-mp="1f0j4bhjuxo4u">
                  {s.photo ? <img src={s.photo} alt={`Portret van ${s.name}`} loading="lazy" className="aspect-[4/5] w-full object-cover" data-id="element-8525" data-mp="30d3rzsgrq4zn" /> :
              // Nog geen foto: haar initialen op een rustig vlak, geen leeg poppetje.
              <span className="flex aspect-[4/5] w-full items-center justify-center bg-blush" aria-hidden="true" data-id="element-8526" data-mp="32w76lwnpk12e">
                      <span className={`${single ? 'type-section' : 'type-sub'} text-ink`} data-id="element-8527" data-mp="37us8s1misuth">{initials(s.name)}</span>
                    </span>}
                  <div className={`min-w-0 ${single ? 'sm:self-center' : ''}`} data-id="element-8528" data-mp="2xzqhp08opio9">
                    <h3 className={`${single ? 'type-sub' : 'type-title'} text-ink`} data-id="element-8529" data-mp="3lnroo4q7sr1k">{s.name}</h3>
                    {s.intro && <p className="mt-1.5 text-[15px] text-ink" data-id="element-8530" data-mp="3qnmfy9dwjin5">{s.intro}</p>}
                    {s.bio && <p className="mt-4 max-w-xl whitespace-pre-line text-[17px] leading-relaxed text-muted" data-id="element-8531" data-mp="3cwhlrmtazik7">{s.bio}</p>}
                    {does.length > 0 && <ul aria-label={`Behandelingen van ${s.name}`} className="mt-5 flex flex-wrap gap-2" data-id="element-8532" data-mp="3gv6tozvko7wg">
                        {does.map(name => <li key={name} className="rounded-full border border-ink/15 px-3 py-1 text-[13px] text-ink" data-id="element-8533" data-mp="d99kie3e9vn8">
                            {name}
                          </li>)}
                      </ul>}
                    <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={`mt-5 ${quietLink} text-ink underline decoration-ink/25 hover:decoration-ink`} data-id="element-8534" data-mp="2brcvvecs3q5c">
                      {/* De lade kiest (nog) geen collega: beloof dat dus niet in de knop. */}
                      {pageActions.book}
                      <span className="sr-only" data-id="element-8535" data-mp="2zgntimyu14f9"> (bij wie, kies je in de agenda)</span>
                    </button>
                  </div>
                </article>
              </li>;
        })}
        </ul>
      </div>
    </Section>;
}